import { Component, OnDestroy, OnInit } from '@angular/core';
import {NavigationEnd, Router, Scroll} from '@angular/router';
import { BsModalRef, BsModalService } from 'ngx-bootstrap/modal';
import { Subject, Subscription } from 'rxjs';
import {ConfirmPopupComponent, FeedbackPopupComponent} from 'src/app/components';
import { PageNameConstant } from 'src/app/constants/reportPage.constants';
import { Report } from 'src/app/model/report';
import { ReportProvider } from 'src/app/providers';
import {CommonService, LoaderService, ReportService, SocketService, ToasterService, UserService} from 'src/app/services';
import {ConfirmationPopupLargeComponent} from '../../../components/confirmation-popup-large/confirmation-popup-large.component';
import {PageNumbers} from '../../../model/Enums';

@Component({
  selector: 'app-header',
  templateUrl: './header.component.html',
  styleUrls: ['./header.component.scss']
})
export class HeaderComponent implements OnInit, OnDestroy {
  public currentReport: Report;
  private reportSub: Subscription;

  private userSub: Subscription;

  currentPage: string = '';
  loginUser: string = '';
  notificationOpen: boolean = false;
  chatOpen: boolean = false;
  noOfCredits: number = 2;
  adminSettingsActive: boolean = false;
  headerSettingButtonState = {
    reports: false,
    users: false,
    organization: false,
    directory: false,
    online: false
  };

  pageNameForFeedback: string = '';
  pageNameConstant: string = '';
  mobileNavExpand: boolean = false;
  profileNavExpand: boolean = false;
  isCheckoutPage: boolean = false;

  constructor(
    private router: Router,
    public reportService: ReportService,
    public userService: UserService,
    private socketService: SocketService,
    private modalService: BsModalService,
    private reportProvider: ReportProvider,
    private toastr: ToasterService,
    private loaderService: LoaderService,
    private commonService: CommonService,
  ) {
    this.router.events.subscribe((data: any) => {
      let currentUrl = '';
      if (data instanceof NavigationEnd && data.url) {
        currentUrl = data.url;
      }else if(data instanceof Scroll){
        currentUrl = data.routerEvent.url;
      }

      // If routing successful
      if (currentUrl) {
        if (currentUrl.startsWith('/administrator-settings')) {
          const url = currentUrl.split('/')[2];
          this.currentPage = url.split('?')[0];
        } else {
          const url = /[^/]*$/.exec(currentUrl)[0];
          this.currentPage = url.split('?')[0];
          this.getPageDetailsForFeedback();
        }

        if (currentUrl.startsWith('/report/payment/checkout/report-purchase/')) {
          this.isCheckoutPage = true;
        }else{
          this.isCheckoutPage = false;
        }
      }

    });
  }

  ngOnInit(): void {
    this.reportSub = this.reportService.report$.subscribe(currentReport => {
      this.currentReport = currentReport;
    });

    this.userSub = this.userService.user$.subscribe(user => {
      if (user) {
        this.socketService.unsubscribeFromUser();
        this.socketService.subscribeToUser(user.ID);

        if(this.userService.checkAdminRole()) {
          this.adminSettingsActive = JSON.parse(localStorage.getItem("administratorSettingActive"));
        }
      }
    });
  }

  ngOnDestroy(): void {
    if (this.reportSub)
      this.reportSub.unsubscribe();
  }

  isNotificationOpenChange(event) {
    this.notificationOpen = event;
  }

  isChatOpenChange(event) {
    this.chatOpen = event;
  }

  /**
   * @description for admin setting open close
   */
  adminSettingClick() {
    this.adminSettingsActive = !this.adminSettingsActive;
    localStorage.setItem('administratorSettingActive', JSON.stringify(this.adminSettingsActive));
    if (!this.adminSettingsActive) {
      this.router.navigate(['/dashboard']);
    } else {
      this.router.navigate(['/administrator-settings/reports'])
    }
  }

  /**
   * @description get page name for feedback
   */
  getPageDetailsForFeedback() {
    switch (this.currentPage) {
      case 'overview':
        this.pageNameForFeedback = 'Overview';
        this.pageNameConstant = PageNameConstant.OVERVIEW;
        break;
      case 'location':
        this.pageNameForFeedback = 'Location';
        this.pageNameConstant = PageNameConstant.LOCATION;
        break;
      case 'ownership':
        this.pageNameForFeedback = 'Ownership';
        this.pageNameConstant = PageNameConstant.OWNERSHIP;
        break;
      case 'income':
        this.pageNameForFeedback = 'Income';
        this.pageNameConstant = PageNameConstant.INCOME;
        break;
      case 'future-projection':
        this.pageNameForFeedback = 'Future Projection';
        this.pageNameConstant = PageNameConstant.FUTURE_PROJECTION;
        break;
      case 'rent-details':
        this.pageNameForFeedback = 'Rent';
        this.pageNameConstant = PageNameConstant.RENT;
        break;
      case 'manager-salary':
      case 'replacement-salary':
        this.pageNameForFeedback = 'Salary';
        this.pageNameConstant = PageNameConstant.OWNERS_SALARY;
        break;
      case 'adjustments':
        this.pageNameForFeedback = 'Adjustments';
        this.pageNameConstant = PageNameConstant.ADJUSTMENTS;
        break;
      case 'weighting-table':
        this.pageNameForFeedback = 'Weighting';
        this.pageNameConstant = PageNameConstant.WEIGHTINGS;
        break;
      case 'projection-table':
        this.pageNameForFeedback = 'Projections';
        this.pageNameConstant = PageNameConstant.PROJECTION;
        break;
      case 'assets-liabilities':
        this.pageNameForFeedback = 'Assets / Liabilities';
        this.pageNameConstant = PageNameConstant.ASSETS_LIABILITIES;
        break;
      case 'diversification':
        this.pageNameForFeedback = 'Diversification / Concentration';
        this.pageNameConstant = PageNameConstant.DIVERSIFICATION;
        break;
      case 'personal-goodwill':
        this.pageNameForFeedback = 'Personal Goodwill';
        this.pageNameConstant = PageNameConstant.PERSONAL_GOODWILL;
        break;
      case 'barriers-entry':
        this.pageNameForFeedback = 'Barriers to Entry';
        this.pageNameConstant = PageNameConstant.BARRIERS_ENTRY;
        break;
      case 'recurring-revenue':
        this.pageNameForFeedback = 'Recurring Revenue';
        this.pageNameConstant = PageNameConstant.RECURRING_REVENUE;
        break;
      case 'conclusion':
        this.pageNameForFeedback = 'Final Value';
        this.pageNameConstant = PageNameConstant.CONCLUSION;
        break;
      case 'express-value':
        this.pageNameForFeedback = 'Express Valuation';
        this.pageNameConstant = PageNameConstant.EXPRESS_VALUE;
        break;
      case 'transactions-history':
        this.pageNameForFeedback = 'Transactions History';
        this.pageNameConstant = PageNameConstant.TRANSACTIONS_HISTORY;
        break;
      case 'account-setting':
        this.pageNameForFeedback = 'Account Setting';
        this.pageNameConstant = PageNameConstant.ACCOUNT_SETTING;
        break;
      case 'required-information':
        this.pageNameForFeedback = 'Introduction';
        this.pageNameConstant = PageNameConstant.INTRODUCTION;
        break;
      case 'checkout':
          this.pageNameForFeedback = 'Checkout';
          this.pageNameConstant = PageNameConstant.CHECKOUT;
          break;
      case 'dashboard-report':
        this.pageNameForFeedback = 'Report Versions';
        this.pageNameConstant = PageNameConstant.REPORT_VERSIONS;
        break;
      case 'pricing':
        this.pageNameForFeedback = 'Pricing';
        this.pageNameConstant = PageNameConstant.PRICING;
        break;
      case 'dashboard':
      default:
        this.pageNameForFeedback = 'Dashboard';
        this.pageNameConstant = PageNameConstant.DASHBOARD;
        break;
    }
  }

  /**
   * @description for feedback
   */
   openFeedbackPopup() {
    let confirmModalRef: BsModalRef;
    confirmModalRef = this.modalService.show(FeedbackPopupComponent, Object.assign({
      initialState: {
        pageName: this.pageNameForFeedback
      }
    }, { class: 'modal-lg modal-dialog-centered' }));

    const ackSubject = new Subject<any>();
    confirmModalRef.content.subject = ackSubject;

    ackSubject.subscribe(obj => {
      if (obj.success) {
        this.submitFeedback(obj);
      }
    });
  }

  /**
   * @description for submit feedback
   */
  submitFeedback(obj: any) {
    const param = {
      PAGE: this.pageNameConstant,
      TEXT: obj.feedbackText,
      CONTACT: obj.feedbackContact
    }
    this.reportProvider.addFeedback(param).subscribe(res => {
      if (res.success) {
        this.toastr.success('Feedback submitted!');
      } else {
        this.toastr.error(res.error.txt);
      }
    }, err => {
      // Error Handling
    });
  }

  logOutClick(): void {
    localStorage.removeItem('IV-TOKEN');
    localStorage.removeItem('IV-PROFILE');
    this.router.navigate(['/login']);
    this.commonService.oneTimeUserPromoCode = '';
  }

  updateAlgVersion() {
    let confirmModalRef: BsModalRef;
    confirmModalRef = this.modalService.show(ConfirmationPopupLargeComponent, Object.assign({
      initialState: {
        title: 'Upgrade Report Algorithm Version',
        message: `Do you want to upgrade Report Algorithm Version to Latest ${this.currentReport.CURRENT_ALG_VERSION}?` +
          '<br>' +
          'Please note that Upgrade might impact final value of the Report, the page progress will be reset to page 1 but all data will be kept so you can revisit pages for any adjustments.',
        confirmButtonText: 'Upgrade',
        showInnerHtml: true
      }
    }, { class: 'modal-lg modal-dialog-centered' }));

    const ackSubject = new Subject<boolean>();
    confirmModalRef.content.subject = ackSubject;

    ackSubject.subscribe(value => {
      if (value) {
        const loadingObj = {
          loading: true,
          loadingMessage: 'Updating Report...'
        };
        this.loaderService.isLoading.next(loadingObj);
        this.reportProvider.updateVersionOfReport(this.currentReport.ID, this.currentReport.LATEST.ID).subscribe(res => {
          if (res.success) {
            this.reportService.updateMaxStage(PageNumbers.OVERVIEW, true);
            this.router.navigateByUrl(`/report/${this.currentReport.ID}/overview`);
            setTimeout(() => {
              const currentUrl = this.router.url;
              this.router.navigateByUrl('/', { skipLocationChange: true }).then(() => {
                this.router.navigate([currentUrl]);
              });
            }, 500);
          } else {
            this.toastr.error(res.error.txt);
          }
          const loadingObj2 = {
            loading: false,
            loadingMessage: ''
          };
          setTimeout(() => {
            this.loaderService.isLoading.next(loadingObj2);
          }, 600);
        }, err => {
          // Error Handling
        });
      }
    });
  }

  /** toggle mobile nav */
  toggleMobileNav(setValue?: boolean): void{
    if(setValue === false || setValue === true){
      this.mobileNavExpand = setValue;
    }else {
      this.mobileNavExpand = !this.mobileNavExpand;
    }
  }

  /** toggle profile navigation */
  toggleProfileNav(): void{
    this.profileNavExpand = !this.profileNavExpand;
  }
}
